/**
 * Pure view helpers for the Quality Triage shell (the 2026-08-25 reimagining,
 * review 3ofroc): the door lands on the triage QUEUE, cases/routed/briefs/ask
 * are tabs, and the header carries alert badges. Kept out of the components so
 * the lane math, routed-away detection and badge counts are testable without a
 * DOM (repo convention: logic in .ts, components in .tsx).
 */
import type {
  DoorInvestigationRead,
  DoorThreadRead,
} from '../../services/qcpInvestigations';
import { isActiveCase } from '../../components/portal/investigationsView';
import {
  hasPostClosureNote,
  hasUnseenUpdate,
  isRoutedToUser,
  seenSnapshot,
  shortDate,
} from '../../components/portal/threadTriageView';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import { waitingOrder } from './waitingOnView';
import { askedSinceOf, waitingWho } from './askWaitView';

/* ---------------------------------------------------------------- */
/* Whose queue is a thread in?                                       */
/* ---------------------------------------------------------------- */

// Queue membership (nameTokens / isRoutedToUser) lives in
// threadTriageView.ts now, so the board's routed lane applies the SAME rule
// as the shell's queue — re-exported here to keep existing imports working.
export { isRoutedToUser, nameTokens } from '../../components/portal/threadTriageView';

/* ---------------------------------------------------------------- */
/* Queue lanes                                                       */
/* ---------------------------------------------------------------- */

const LIVE_STATUSES: ReadonlyArray<DoorThreadRead['status']> = ['routed', 'open'];

/** Live threads in the door user's own queue (order preserved from input). */
export function myQueueThreads(
  threads: DoorThreadRead[],
  userName: string | null | undefined,
): DoorThreadRead[] {
  return threads.filter(
    (t) => LIVE_STATUSES.includes(t.status) && isRoutedToUser(t, userName),
  );
}

export interface QueueLanes {
  /** "Waiting on your call" — high/critical severity, a decision is due. */
  decide: DoorThreadRead[];
  /** "Waiting on someone else" — the reader said who it waits on (case
   *  round, finding 4), or it has an open ask out to someone (UX round
   *  2026-10-06), so it is not the reader's call right now. Still one of
   *  the reader's items: listed on Triage in its own lane, not counted in
   *  the "your call" number. */
  waiting: DoorThreadRead[];
  /** "Watching" — Merlin tracks these; nothing to decide yet. */
  watching: DoorThreadRead[];
}

/** A thread that needs the user's call: high/critical severity, or Merlin
 *  marked it escalating (turn 1: an escalating item sat under "Watching —
 *  nothing for you to decide" and opened red ESCALATING). */
function needsCall(t: DoorThreadRead): boolean {
  return t.severity === 'high' || t.severity === 'critical' || t.work_state === 'escalating';
}

/** Split the user's live queue into the landing lanes. `requests` are the
 *  door's asks: an item with an open one waits on whoever was asked. */
export function queueLanes(
  threads: DoorThreadRead[],
  userName: string | null | undefined,
  requests: DoorDataRequestRead[] = [],
): QueueLanes {
  const mine = myQueueThreads(threads, userName);
  const waits = (t: DoorThreadRead) => waitingWho(t, requests).length > 0;
  const parked = mine.filter(waits);
  const rest = mine.filter((t) => !waits(t));
  return {
    decide: rest.filter(needsCall),
    waiting: parked,
    watching: rest.filter((t) => !needsCall(t)),
  };
}

/* ---------------------------------------------------------------- */
/* Triage's one list (proposal 4)                                    */
/* ---------------------------------------------------------------- */

export type TriageLane = 'yourCall' | 'ourCases' | 'waiting' | 'watching';

export interface TriageList {
  /** "Waiting on your call": the heading count, its cards, the Triage tab
   *  count and the pager opened from one of them are all this list. */
  yourCall: DoorThreadRead[];
  /** "Open cases where the next step is ours" (the Cases board's Our court). */
  ourCases: DoorInvestigationRead[];
  /** "Waiting on someone else", past their date first. */
  waiting: DoorThreadRead[];
  /** "Watching". */
  watching: DoorThreadRead[];
  /** Each lane's item numbers in the order its cards show: the pager opened
   *  from a card walks that card's lane, so "1 of N" counts the cards under
   *  the heading it was opened from. */
  order: Record<TriageLane, string[]>;
  /** Every item on the "All my items" page, in page order: its rail count
   *  is the sum of the lane counts, so the rail never says more than the
   *  page shows. */
  all: string[];
}

/**
 * The one computed list behind Triage (proposal 4, kanban 45a002d6). A
 * tester saw "Waiting on your call 1" while the pager said "1 of 3": the
 * heading counted one lane and the pager walked the whole page. Now the
 * heading, its cards, the rail, the tab and the pager read this.
 */
export function triageList(
  threads: DoorThreadRead[],
  investigations: DoorInvestigationRead[],
  userName: string | null | undefined,
  requests: DoorDataRequestRead[] = [],
  now: Date = new Date(),
): TriageList {
  const lanes = queueLanes(threads, userName, requests);
  const ourCases = caseColumns(investigations).ours;
  const waiting = waitingOrder(lanes.waiting, now, askedSinceOf(requests));
  const order: Record<TriageLane, string[]> = {
    yourCall: lanes.decide.map((t) => t.thread_no),
    ourCases: ourCases.map((i) => i.investigation_no),
    waiting: waiting.map((t) => t.thread_no),
    watching: lanes.watching.map((t) => t.thread_no),
  };
  return {
    yourCall: lanes.decide,
    ourCases,
    waiting,
    watching: lanes.watching,
    order,
    all: [...order.yourCall, ...order.ourCases, ...order.waiting, ...order.watching],
  };
}

/** Triage's "Waiting on others" filter (waiting filter round; a tab of its
 *  own in the tabs round): the reader's own items parked on a named person,
 *  past their date first. The same set as the page's "Waiting on someone
 *  else" lane. */
export function waitingOnOthers(
  threads: DoorThreadRead[],
  userName: string | null | undefined,
  now: Date = new Date(),
  requests: DoorDataRequestRead[] = [],
): DoorThreadRead[] {
  return waitingOrder(queueLanes(threads, userName, requests).waiting, now, askedSinceOf(requests));
}

/** Live threads sent AWAY from the door user (the "Routed away" tab). */
export function routedAwayThreads(
  threads: DoorThreadRead[],
  userName: string | null | undefined,
): DoorThreadRead[] {
  return threads.filter(
    (t) => LIVE_STATUSES.includes(t.status) && !isRoutedToUser(t, userName),
  );
}

/** Dismissed threads, with their calibration reasons (Routed away tab). */
export function dismissedThreads(threads: DoorThreadRead[]): DoorThreadRead[] {
  return threads.filter((t) => t.status === 'dismissed');
}

/** Threads closed as done — real issues, handled straight from triage
 *  (Jess's Done button; deliberately NOT mixed in with dismissals). */
export function completedThreads(threads: DoorThreadRead[]): DoorThreadRead[] {
  return threads.filter((t) => t.status === 'completed');
}

/* ---------------------------------------------------------------- */
/* Reopened threads + notes after closure (Scott, 2026-09-08)        */
/* ---------------------------------------------------------------- */

// Shared with the triage board (components/portal) — lives in
// threadTriageView.ts so the board never imports from pages/.
export {
  hasPostClosureNote,
  isReopened,
  postClosureLine,
  reopenedLine,
  shortDate,
} from '../../components/portal/threadTriageView';

/* ---------------------------------------------------------------- */
/* Cases columns (whose court)                                       */
/* ---------------------------------------------------------------- */

/** Completed on our side, the Sigma effectiveness window still open: Jess's
 *  "done on our end, just waiting for the effectiveness timer to run out"
 *  (kanban d91f705b). The server derives it from the watch stamps. */
export function isWatchingCase(inv: Pick<DoorInvestigationRead, 'court'>): boolean {
  return inv.court.side === 'watching';
}

/**
 * The Cases board's one list (proposal 5): every open case (not cancelled,
 * not closed) plus the completed cases still inside their effectiveness
 * watch (kanban d91f705b: a stage before close, not after it), oldest
 * first. The Cases tab badge, the rail counts, the column counts and
 * Triage's "our cases" all read this, so they are one number.
 */
export function caseBoard(investigations: DoorInvestigationRead[]): DoorInvestigationRead[] {
  return investigations
    .filter((i) => (isActiveCase(i) || isWatchingCase(i)) && i.court.side !== 'done')
    .sort((a, b) => a.created_at.localeCompare(b.created_at));
}

export interface CaseColumns {
  /** Our court: no ask is out, the next step is ours. */
  ours: DoorInvestigationRead[];
  /** Their court: an ask is open with a named person. */
  theirs: DoorInvestigationRead[];
  /** Ready to close: marked for sign-off, nothing out. */
  ready: DoorInvestigationRead[];
  /** Waiting on effectiveness: fixed on our end, watching for a repeat. */
  watching: DoorInvestigationRead[];
}

/** The board's columns, by the court the server derived (`court.side`),
 *  each oldest first. Closed cases (done) are not on it. */
export function caseColumns(
  investigations: DoorInvestigationRead[],
): CaseColumns {
  const board = caseBoard(investigations);
  return {
    ours: board.filter((i) => i.court.side === 'ours'),
    theirs: board.filter((i) => i.court.side === 'theirs'),
    ready: board.filter((i) => i.court.side === 'ready'),
    watching: board.filter(isWatchingCase),
  };
}

/** The board's columns in reading order, flattened — the previous / next
 *  walk from an opened case. */
export function boardOrder(cols: CaseColumns): DoorInvestigationRead[] {
  return [...cols.ours, ...cols.theirs, ...cols.ready, ...cols.watching];
}

/**
 * "Checklist: 3 of 5 done" — Scott asked what the progress bar meant, so the
 * label now says exactly what it counts. Cancelled items don't count either
 * way; null when the case has no checklist at all (render nothing, not 0/0).
 */
export function checklistProgress(
  investigation: Pick<DoorInvestigationRead, 'tasks'>,
): { done: number; total: number } | null {
  const counted = investigation.tasks.filter((t) => t.status !== 'cancelled');
  if (counted.length === 0) return null;
  return {
    done: counted.filter((t) => t.status === 'done').length,
    total: counted.length,
  };
}

/* ---------------------------------------------------------------- */
/* Tab alert badges                                                  */
/* ---------------------------------------------------------------- */

export interface TriageTabAlerts {
  /** Threads waiting on the user's call (Triage tab count pill). The
   *  Watching lane is not counted: the page says nothing there needs a
   *  decision, so a count that included it read as "waiting on you". */
  queueCount: number;
  /** Of those, the ones Merlin touched since last read here (red badge). */
  queueUnseen: number;
  /** Cases on the board, caseBoard (Cases tab count pill). */
  casesActive: number;
  /** Cases holding an unviewed ask answer (Cases tab red badge). */
  casesUnseen: number;
  /** Live threads sent away (Routed away tab count pill). */
  routedAway: number;
  /** Closed threads — Done + Dismissed (Done tab count pill). */
  doneCount: number;
  /** Closed threads Merlin annotated after closure (Done tab red badge). */
  doneNotes: number;
}

/** Every human-closed thread — Done (real, handled) and Dismissed (not an
 *  issue) — the Done tab's population. Scott, 2026-09-08: "I don't see the
 *  Done category" — they had been parked under Routed away. */
export function closedThreads(threads: DoorThreadRead[]): DoorThreadRead[] {
  return threads.filter((t) => t.status === 'completed' || t.status === 'dismissed');
}

export function tabAlerts(
  threads: DoorThreadRead[],
  investigations: DoorInvestigationRead[],
  userName: string | null | undefined,
  seen: Record<string, string> = seenSnapshot(),
  requests: DoorDataRequestRead[] = [],
): TriageTabAlerts {
  // The page's own list (proposal 4): the tab count is the heading count.
  const decide = triageList(threads, investigations, userName, requests).yourCall;
  const board = caseBoard(investigations);
  const closed = closedThreads(threads);
  return {
    queueCount: decide.length,
    queueUnseen: decide.filter((t) => hasUnseenUpdate(t, seen)).length,
    casesActive: board.length,
    casesUnseen: board.filter((i) => i.unseen_response_count > 0).length,
    routedAway: routedAwayThreads(threads, userName).length,
    // Everything the Done tab lists: closed items plus completed cases that
    // no closed item already stands for (turn 5: completing a case left the
    // count unchanged). A case still in its effectiveness watch is on the
    // Cases board, not here.
    doneCount:
      closed.length +
      completedCases(investigations).filter(
        (i) => !closed.some((t) => t.investigation_id === i.id),
      ).length,
    // Only notes not yet read: opening the item marks it seen, which clears
    // it from the badge (turn 4: "12 new notes" could never be cleared).
    doneNotes: closed.filter((t) => hasPostClosureNote(t) && hasUnseenUpdate(t, seen)).length,
  };
}

/* ---------------------------------------------------------------- */
/* Looking back: the Done tab's search                               */
/* ---------------------------------------------------------------- */

/** Completed case files whose effectiveness watch has ended — the other
 *  half of "the record". They used to live only on the Cases board, which
 *  has no search, so a past complaint closed as a case could not be found
 *  from Done. A completed case still inside its watch is not closed yet
 *  (kanban d91f705b): it stays on the Cases board and lands here on its
 *  own once the window runs out. */
export function completedCases(investigations: DoorInvestigationRead[]): DoorInvestigationRead[] {
  return investigations.filter((i) => i.status === 'completed' && !isWatchingCase(i));
}

/** True when every word of `query` appears somewhere in the item: its number,
 *  title, the text written on it and its keys (customer, product, lot,
 *  order). Empty query matches everything. */
function matchesWords(haystack: string, query: string): boolean {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const text = haystack.toLowerCase();
  return words.every((w) => text.includes(w));
}

export function threadMatchesSearch(thread: DoorThreadRead, query: string): boolean {
  return matchesWords(
    [
      thread.thread_no,
      thread.title,
      thread.summary,
      thread.routing_reason ?? '',
      thread.dismissed_reason ?? '',
      thread.work_state_note,
      ...Object.values(thread.bridge_keys ?? {}).map(String),
    ].join(' '),
    query,
  );
}

export function caseMatchesSearch(inv: DoorInvestigationRead, query: string): boolean {
  return matchesWords(
    [
      inv.investigation_no,
      inv.title,
      inv.context_summary,
      inv.findings,
      inv.resolution_summary ?? '',
      ...inv.remediation_details,
      ...Object.values(inv.bridge_keys ?? {}).map(String),
      ...inv.tasks.map((t) => t.label),
    ].join(' '),
    query,
  );
}

/* ---------------------------------------------------------------- */
/* Row copy                                                          */
/* ---------------------------------------------------------------- */

/** "Marked Done Sep 2 — real issue, handled" / "Not an issue — <reason> · Sep 4"
 *  — the line under a Done-tab row, with how often it had come back before. */
export function closedLine(
  thread: Pick<
    DoorThreadRead,
    'status' | 'dismissed_reason' | 'resolved_at' | 'reopened_count'
  >,
): string {
  const when = shortDate(thread.resolved_at);
  const cameBack =
    (thread.reopened_count ?? 0) > 0
      ? ` · came back ${thread.reopened_count}× before`
      : '';
  if (thread.status === 'dismissed') {
    const reason = (thread.dismissed_reason ?? '').trim() || 'no reason recorded';
    return `Not an issue — ${reason}${when ? ` · ${when}` : ''}${cameBack}`;
  }
  return `Marked Done${when ? ` ${when}` : ''} — real issue, handled${cameBack}`;
}

/** "→ Laura, Customer Service & Hadi · 2d with them" — the Routed away line. */
export function routedAwayLine(
  thread: Pick<DoorThreadRead, 'routed_to' | 'routing_reason'> & Partial<Pick<DoorThreadRead, 'route_notified'>>,
): string {
  const to = (thread.routed_to ?? '').trim();
  const reason = (thread.routing_reason ?? '').trim();
  if (!to) return '';
  // "Sent" is a lie for an item the system assigned and never emailed
  // (card 75bf026f): say what happened.
  const verb = thread.route_notified === 'not_sent' ? 'Assigned to' : 'Sent to';
  return reason ? `${verb} ${to} — "${reason}"` : `${verb} ${to}`;
}
